import { differenceInCalendarDays } from 'date-fns';
import { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import TagManager from 'react-gtm-module';
import { ProfileRootType } from '../../@types/auth';
import { accessToken, getProfile } from '../../api/auth';
import { getUserNotiSetting } from '../../api/login';
import { getOrderList } from '../../api/orderHistory';
import { sendSetAppsflyerEvent } from '../../js/postMessage';
import { getParsedLocalStorageItem, getParsedSessionStorageItem } from '../../js/script';
import { useAppContext } from './AppProvider';
import { useCommonContext } from './CommonProvider';

const PromotionContext = createContext<{
  promotionTitle: any;
  setPromotionTitle: Dispatch<SetStateAction<any>>;
}>({
  promotionTitle: null,
  setPromotionTitle: () => {},
});

type promotionValueType = string | null;

const PromotionProvider = ({ children }: { children: any }) => {
  const [promotionTitle, setPromotionTitle] = useState<any>(null);
  const [gtmFired, setGtmFired] = useState(false);
  const [profileData, setProfileData] = useState<ProfileRootType | undefined>();
  const [orderData, setOrderData] = useState<any>();
  const [userPay, setUserPay] = useState(0);
  const [marketing, setMarketing] = useState<any>(null);
  const [joinBetween, setJoinBetween] = useState<number>(0);
  const [loginBetween, setLoginBetween] = useState<number>(0);

  const { isAppView, setStorageAppValue } = useAppContext();
  const { hasAccessToken } = useCommonContext();

  //gtm
  useEffect(() => {
    if (!location.pathname.includes('exhibitiondetail')) {
      setPromotionTitle(null);
    }
  }, [location]);

  useEffect(() => {
    const result = getParsedSessionStorageItem('app');
    setStorageAppValue(result);
  }, [location]);

  useEffect(() => {
    if (hasAccessToken) {
      getProfile().then((res) => {
        setProfileData(res.data);
      });
      getOrderList().then((res) => {
        setOrderData(res.data);

        // reduce를 사용하여 lastOrderAmt.payAmt의 총합을 계산
        const totalPay = res.data.items.reduce((acc: number, el: any) => acc + el.lastOrderAmt.payAmt, 0);

        setUserPay(totalPay);
      });
      getUserNotiSetting().then((res) => {
        setMarketing(res.data);
      });
    }
  }, []);

  useEffect(() => {
    if (profileData) {
      const today = new Date();
      const betweenDate = daysBetweenDates(profileData.joinYmdt, today);
      setJoinBetween(betweenDate);
      const loginDate = daysBetweenDates(profileData.lastLoginYmdt, today);
      setLoginBetween(loginDate);
    }
  }, [profileData]);

  // 날짜 차이를 계산하는 함수
  function daysBetweenDates(startDate: any, today: Date) {
    return differenceInCalendarDays(today, new Date(startDate));
  }

  useEffect(() => {
    const app = getParsedLocalStorageItem('app');

    const createGtmPageView = (promotionId: promotionValueType, promotionName: promotionValueType) => ({
      dataLayer: {
        event: 'page_view',
        page_info: {
          page_url: location.pathname,
          page_title: document.title,
          page_referrer: document.referrer,
        },
        platform_info: {
          device_type: isMobile ? (app ? 'Mobile_APP' : 'Mobile_WEB') : 'PC_WEB',
        },
        user_info: {
          login_status: hasAccessToken ? 'Y' : 'N',
          user_id: profileData?.memberId,
          sign_up_date: profileData?.joinYmdt,
          days_from_sign_up: joinBetween,
          days_from_last_visit: loginBetween,
          total_purchases_count: orderData?.totalCount,
          total_purchases_revenue: userPay,
          marketing_consent_status: marketing?.isMarketing ? 'Y' : 'N',
          push_consent_status: profileData?.pushNotificationAgreed ? 'Y' : 'N',
          sms_consent_status: profileData?.smsAgreed ? 'Y' : 'N',
          member_grade: profileData?.memberGradeName,
        },
        event_info: {
          promotion_id: promotionId,
          promotion_name: promotionName,
        },
      },
    });

    const createAppsflyerEvent = (promotionId: promotionValueType, promotionName: promotionValueType) => ({
      eventName: 'page_view',
      eventType: {
        page_url: location.pathname,
        page_title: document.title,
        page_referrer: document.referrer,
        device_type: isMobile ? (app ? 'Mobile_APP' : 'Mobile_WEB') : 'PC_WEB',
        login_status: hasAccessToken ? 'Y' : 'N',
        user_id: profileData?.memberId,
        sign_up_date: profileData?.joinYmdt,
        days_from_sign_up: joinBetween,
        days_from_last_visit: loginBetween,
        total_purchases_count: orderData?.totalCount,
        total_purchases_revenue: userPay,
        marketing_consent_status: marketing?.isMarketing ? 'Y' : 'N',
        push_consent_status: profileData?.pushNotificationAgreed ? 'Y' : 'N',
        sms_consent_status: profileData?.smsAgreed ? 'Y' : 'N',
        member_grade: profileData?.memberGradeName,
        promotion_id: promotionId,
        promotion_name: promotionName,
      },
    });

    const sendEvent = (promotionId: promotionValueType, promotionName: promotionValueType) => {
      const gtmPageView = createGtmPageView(promotionId, promotionName);
      if (isAppView) {
        sendSetAppsflyerEvent(createAppsflyerEvent(promotionId, promotionName));
      } else {
        TagManager.dataLayer(gtmPageView);
      }
      setGtmFired(true);
    };
    if (!gtmFired) {
      if (location.pathname.includes('exhibitiondetail')) {
        if (hasAccessToken && profileData && orderData && marketing && promotionTitle) {
          sendEvent(String(promotionTitle?.promotion_id), promotionTitle?.promotion_name);
        } else if (!hasAccessToken && promotionTitle) {
          sendEvent(String(promotionTitle?.promotion_id), promotionTitle?.promotion_name);
        }
      } else {
        if (hasAccessToken && profileData && orderData && marketing) {
          sendEvent(null, null);
        } else if (!hasAccessToken) {
          sendEvent(null, null);
        }
      }
    }

    return () => setGtmFired(false); // 컴포넌트 언마운트 시 플래그 초기화
  }, [location.pathname, profileData, orderData, joinBetween, marketing, userPay, promotionTitle, accessToken]);

  return (
    <PromotionContext.Provider
      value={{
        promotionTitle,
        setPromotionTitle,
      }}
    >
      {children}
    </PromotionContext.Provider>
  );
};

const usePromotionContext = () => {
  const context = useContext(PromotionContext);
  if (!context) {
    throw new Error('usePromotionContext 가 PromotionProvider 안에서 사용되지 않았습니다.');
  }
  return context;
};

export { PromotionContext, PromotionProvider, usePromotionContext };
